import { Box, Button, Flex, Heading, HStack, Spacer, Text, Tooltip, VStack } from '@chakra-ui/react'
import { faPlus, faRandom } from '@fortawesome/pro-regular-svg-icons'
import { type ReactElement, useEffect } from 'react'

import logger from '@/utils/logger'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'

import { useToaster } from '@/components/Toast/useToaster'
import Alert from '@/components/UI/Alert'
import Icon from '@/components/UI/Icon/Icon'
import useRealtimeManageBreakoutSessions from '@/components/Worlds/hooks/session/useRealtimeManageBreakoutSessions'
import Card from '@/components/Worlds/UI/Card'

import HostCard from './HostCard'
import RegistrationCard from './RegistrationCard'

export default function BreakoutSessionManageBanner({
  sessionId,
}: {
  sessionId: number
}): ReactElement {
  const toast = useToaster()
  const { error, session } = useRealtimeManageBreakoutSessions(sessionId)

  useEffect(() => {
    if (error != null) {
      logger.error('Error loading registrations', { error })
    }
  }, [error])

  const breakoutSessions = session?.session_breakouts ?? []
  const breakoutSessionIds = breakoutSessions.map(breakoutSession => breakoutSession.id)

  const assignedUserIds = breakoutSessions.flatMap(breakoutSession =>
    breakoutSession.session_registrations.map(reg => reg.user.id)
  )

  const unassignedRegistrations =
    session?.session_registrations.filter(
      reg => reg.user?.id !== null && !assignedUserIds.includes(reg.user.id)
    ) ?? []

  const sessionHosts = session?.session_hosts ?? []
  const unassignedHosts = sessionHosts.filter(
    host => host.user?.id !== null && !assignedUserIds.includes(host.user.id)
  )

  const [handleCreateBreakout] = useAPIWrapper(
    async () => {
      toast({
        title: 'Creating breakout session...',
        status: 'info',
      })

      await RPC.createBreakoutSession({ sessionId })

      toast({
        title: 'Breakout session created successfully',
        status: 'success',
      })
    },
    [sessionId, toast],
    'Create Breakout Session'
  )

  const [handleCreateAndAssignBreakoutRooms] = useAPIWrapper(
    async () => {
      if (unassignedRegistrations.length === 0) {
        toast({
          title: 'No learners to assign',
          status: 'warning',
        })
        return
      }

      toast({
        title: 'Creating and assigning breakout rooms...',
        status: 'info',
      })

      await RPC.createAndAssignBreakoutRooms({ sessionId })

      toast({
        title: 'Breakout rooms created and users assigned',
        status: 'success',
      })
    },
    [sessionId, toast, unassignedRegistrations.length],
    'Create and Assign Breakout Rooms'
  )

  return (
    <Card width="100%" mb={4}>
      {error != null ? (
        <Alert variant="error">
          There was an error loading the breakout sessions. Please try again later.
        </Alert>
      ) : (
        <VStack align="stretch" spacing={6}>
          <Heading size="lg">{session?.session_registrations?.length ?? 0} Learners Joined</Heading>
          <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
            <VStack width={{ md: '50%' }}>
              <HStack width="100%" h="40px" align="center">
                <Heading size="md">Unassigned Participants</Heading>
              </HStack>

              {unassignedRegistrations.length === 0 && unassignedHosts.length === 0 ? (
                <Box textAlign="left" width="100%" color="fg.muted">
                  <Text>No unassigned participants have joined yet.</Text>
                </Box>
              ) : (
                <>
                  {unassignedHosts.map(host => (
                    <HostCard
                      key={`host-${host.user.id}`}
                      host={host}
                      breakoutSessionIds={breakoutSessionIds}
                    />
                  ))}
                  {unassignedRegistrations.map(registration => (
                    <RegistrationCard
                      key={`reg-${registration.id}`}
                      registration={registration}
                      breakoutSessionIds={breakoutSessionIds}
                    />
                  ))}
                </>
              )}
            </VStack>

            <VStack width={{ md: '50%' }}>
              <HStack width="100%" h="40px" align="center">
                <Heading size="md">Breakouts</Heading>
                <Spacer />
                <Tooltip
                  label="Auto-create rooms and assign unassigned learners"
                  placement="top"
                  hasArrow
                >
                  <Button
                    variant="ghost"
                    color="primary.fg"
                    onClick={handleCreateAndAssignBreakoutRooms}
                    leftIcon={<Icon faIcon={faRandom} />}
                  >
                    Create & Assign
                  </Button>
                </Tooltip>
              </HStack>
              <VStack width="100%">
                <Box maxHeight="500px" overflowY="auto" width="100%">
                  {breakoutSessions.length === 0 ? (
                    <Box textAlign="left" width="100%" color="fg.muted">
                      No breakout rooms have been created yet.
                    </Box>
                  ) : (
                    breakoutSessions.map((breakoutSession, index) => (
                      <Card key={`${breakoutSession.id}`} width="100%" mb={3}>
                        <Flex justify="space-between" align="center" mb={3}>
                          <Heading size="sm">Room {index + 1}</Heading>
                        </Flex>

                        <VStack align="stretch" spacing={2}>
                          {breakoutSession.session_registrations.map(registration => (
                            <RegistrationCard
                              key={`${registration.id}`}
                              registration={registration}
                              breakoutSessionIds={breakoutSessionIds}
                            />
                          ))}
                        </VStack>
                      </Card>
                    ))
                  )}
                </Box>
                <Button
                  width="100%"
                  variant="ghost"
                  color="primary.fg"
                  onClick={handleCreateBreakout}
                  leftIcon={<Icon faIcon={faPlus} />}
                >
                  Add Another Room
                </Button>
              </VStack>
            </VStack>
          </Flex>
        </VStack>
      )}
    </Card>
  )
}
